/// <reference types="vite/client" />
import { Suspense, lazy, useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { HelmetProvider } from "@/lib/helmet-compat";
import appCss from "../styles.css?url";
// Bundled (hashed, immutable) font URLs — same asset the @font-face rules in
// styles.css reference, so preload and CSS resolve to one file, not two.

import { AdminBodyClass } from "@/components/admin/AdminBodyClass";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DelayedFallback } from "@/components/ui/delayed-fallback";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { SkipToContent } from "@/components/SkipToContent";
import { PaymentRouteCleanup } from "@/components/payments/PaymentRouteCleanup";
import { useEventMount, useIdleMount } from "@/hooks/useDeferredMount";
import { COACH_KAI_OPEN_EVENT } from "@/lib/coachKai";
import { AppNavigationShell } from "@/components/AppNavigationShell";

const Ga4DebugPanelGate = lazy(() => import("@/components/debug/Ga4DebugPanelGate"));
const GlobalTrackingRuntime = lazy(() =>
  import("@/components/GlobalTrackingRuntime").then((module) => ({ default: module.GlobalTrackingRuntime })),
);
import { getTagConfig } from "@/lib/tagConfig.functions";
import { ViewAsProvider } from "@/contexts/ViewAsContext";
import { NotFound } from "@/lib/lazyPages";
import { reportLovableError } from "@/lib/lovable-error-reporting";

// ported from main.tsx — attribution capture + global JWT guard + preview SW
// cleanup + deferred analytics/PWA-adjacent startup. All strictly browser-only.
import { isPreviewEnv } from "@/lib/isPreviewEnv";
import { clearStaleAuthState, isBackendAuthUrl, isFatalAuthResponseText } from "@/lib/authStorage";
import { clearChunkReloadGuard } from "@/lib/lazyPage";

import { captureAttribution } from "@/lib/attribution";

if (typeof window !== "undefined") {
  // Persist UTM params / referrer from THIS page load before the router can
  // strip the query string — checkout and purchase happen much later.
  captureAttribution();

  // GLOBAL JWT GUARD — narrow: only /auth/v1/user + /auth/v1/token, skipped
  // during OAuth callbacks, 8s boot grace, at most one purge per app instance.
  (() => {
    try {
      if (sessionStorage.getItem("__jwt_purged")) return;

      const isOAuthCallbackNow = () => {
        const hash = window.location.hash || "";
        const search = window.location.search || "";
        return (
          hash.includes("access_token") ||
          hash.includes("error") ||
          search.includes("code=")
        );
      };

      const bootAt = Date.now();
      const BOOT_GRACE_MS = 8_000;
      const origFetch = window.fetch.bind(window);
      let purged = false;

      const isAuthUserOrTokenEndpoint = (url: string): boolean => {
        try {
          const parsed = new URL(url, window.location.origin);
          return (
            parsed.pathname === "/auth/v1/user" ||
            parsed.pathname === "/auth/v1/token" ||
            parsed.pathname.startsWith("/auth/v1/token")
          );
        } catch {
          return false;
        }
      };

      window.fetch = async (...args: Parameters<typeof fetch>) => {
        const res = await origFetch(...args);
        if (purged) return res;
        if (res.status !== 401 && res.status !== 403) return res;
        if (Date.now() - bootAt < BOOT_GRACE_MS) return res;
        if (isOAuthCallbackNow()) return res;
        try {
          const url = typeof args[0] === "string" ? args[0] : (args[0] as Request).url;
          if (!url || !isBackendAuthUrl(url)) return res;
          if (!isAuthUserOrTokenEndpoint(url)) return res;
          const clone = res.clone();
          const text = await clone.text();
          if (!isFatalAuthResponseText(text)) return res;
          purged = true;
          sessionStorage.setItem("__jwt_purged", "1");
          clearStaleAuthState();
          window.location.reload();
        } catch { /* noop */ }
        return res;
      };
    } catch { /* noop */ }
  })();

  // In the Lovable editor preview / iframe, unregister any previously
  // registered service worker so stale Workbox builds never interfere.
  if (isPreviewEnv() && "serviceWorker" in navigator) {
    void navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => void r.unregister());
    });
  }

  // Defer non-critical startup work until the browser is idle.
  const runIdle = (cb: () => void, timeout = 2000) => {
    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (ric) ric(cb, { timeout });
    else setTimeout(cb, 1);
  };

  // Telemetry: wait for FCP, then add ~2.5s buffer before loading.
  const deferAnalytics = () => {
    const load = () => void import("@/lib/analytics").then((m) => m.initAnalytics());
    const POST_FCP_DELAY_MS = 2500;
    try {
      const po = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === "first-contentful-paint") {
            po.disconnect();
            setTimeout(load, POST_FCP_DELAY_MS);
            return;
          }
        }
      });
      po.observe({ type: "paint", buffered: true });
      setTimeout(() => {
        try { po.disconnect(); } catch { /* noop */ }
        load();
      }, 8000);
    } catch {
      setTimeout(load, 4000);
    }
  };
  deferAnalytics();

  // A load with no interaction never loads gtag.js / the Pixel, so report that
  // pageview from the server instead (see src/lib/serverPageview.ts). Scheduled
  // early so short bounces are still counted; it is a keepalive beacon, not a
  // script, so it stays off the critical path and cancels on `lx-tags-loaded`.
  setTimeout(() => {
    void import("@/lib/serverPageview").then((m) => m.scheduleServerPageview());
  }, 500);


  runIdle(() => {
    // Visibility into silent token-refresh failures.
    void import("@/lib/tokenRefreshMonitor").then((m) => m.initTokenRefreshMonitor());
  });

  // Route prefetch costs a ~280ms long task and three page chunks, so it waits
  // for a signal that the visitor is actually going somewhere.
  const prefetch = () =>
    void import("@/lib/routePrefetch").then((m) => m.prefetchLikelyRoutes());
  if ((window as Window & { __lxTagsLoaded?: boolean }).__lxTagsLoaded) runIdle(prefetch);
  else window.addEventListener("lx-tags-loaded", () => runIdle(prefetch), { once: true });
}


// Non-critical global UI. These are rendered by `DeferredGlobalUI` behind mount
// gates (see useDeferredMount) — `lazy()` on its own would still fetch every
// chunk on the critical path of every page load, since they render immediately.
//
// Every loader goes through `safeLazy`: a rotated/evicted chunk (stale service
// worker cache, flaky mobile network) used to resolve to `undefined` and React
// then threw "Cannot read properties of undefined (reading 'default')" while
// rendering the ROOT route — blanking the whole page, including /auth, so the
// visitor could not sign in. These overlays are all optional, so a failed load
// must degrade to rendering nothing.
function safeLazy<T extends Record<string, unknown>>(
  load: () => Promise<T>,
  exportName: keyof T,
) {
  const empty: { default: React.ComponentType<any> } = { default: () => null };
  return lazy(async () => {
    try {
      const mod = await load();
      const cmp = mod?.[exportName];
      if (typeof cmp !== "function") return empty;
      return { default: cmp as React.ComponentType<any> };
    } catch {
      return empty;
    }
  });
}

const ResponsiveViewportNudge = safeLazy(
  () => import("@/components/ResponsiveViewportNudge"),
  "ResponsiveViewportNudge",
);
const PWAUpdatePrompt = safeLazy(() => import("@/components/PWAUpdatePrompt"), "PWAUpdatePrompt");
const NetworkStatusBanner = safeLazy(
  () => import("@/components/NetworkStatusBanner"),
  "NetworkStatusBanner",
);
// Admin-only "view as" ribbon: it renders nothing for everyone else, so its
// auth/role dependencies must not sit in the first-paint module graph.
// NOTE: the local binding is deliberately NOT named `ViewAsBanner`. The SSR
// bundle inlines the component module, and the duplicate top-level name
// produced "ReferenceError: ViewAsBanner is not defined" during
// renderToReadableStream, crashing the whole root layout.
const LazyViewAsBanner = safeLazy(() => import("@/components/ViewAsBanner"), "ViewAsBanner");
// An interrupted payment's confirmation is retried on whatever page the buyer
// lands on next, not only the trial page.
const GlobalPaymentRecovery = safeLazy(
  () => import("@/components/payments/GlobalPaymentRecovery"),
  "GlobalPaymentRecovery",
);
const CoachKaiChatDock = safeLazy(
  () => import("@/components/coach/CoachKaiChatDock"),
  "CoachKaiChatDock",
) as React.ComponentType<{ initialOpen?: boolean }>;

/**
 * Global overlays, kept off the critical path.
 *
 * - The coach dock waits for the first "open coach" event, so neither the chat
 *   panel nor the coach artwork is downloaded on pages where nobody opens it
 *   (the sign-in screen especially).
 * - The viewport nudge, update prompt and network banner wait for window load /
 *   idle / first interaction. None of them can show anything before then.
 */
function DeferredGlobalUI() {
  const idle = useIdleMount();
  const coachRequested = useEventMount(COACH_KAI_OPEN_EVENT);

  return (
    <Suspense fallback={null}>
      {coachRequested ? <CoachKaiChatDock initialOpen /> : null}
      {idle ? (
        <>
          <GlobalPaymentRecovery />
          <LazyViewAsBanner />
          <NetworkStatusBanner />
          <ResponsiveViewportNudge />
          <PWAUpdatePrompt />
        </>
      ) : null}
    </Suspense>
  );
}


/**
 * Tracking and telemetry do not affect the first paint. Mount them after the
 * page has settled so their Supabase, attribution and analytics dependencies
 * are not part of the landing/auth critical module graph.
 */
function DeferredGlobalTracking() {
  const idle = useIdleMount();
  if (!idle) return null;
  return (
    <Suspense fallback={null}>
      <GlobalTrackingRuntime />
    </Suspense>
  );
}

const SITE_TITLE = "LeadXkids — Online Life Skills & Confidence Courses for Kids 8-14";
const SITE_DESCRIPTION =
  "Build confidence, public speaking & communication in kids 8-14. Psychologist-designed, self-paced online courses for Indian children.";
const OG_IMAGE = "https://leadxkids.com/leadxkids-social-banner-2026-10.jpg";
const OG_IMAGE_ALT =
  "LeadXkids — Life Skills for Life: confidence, focus, resilience and communication for children aged 8 to 14";

// Animation features are provided per animated subtree by
// `@/components/motion/MotionRoot`, so framer-motion stays out of the shared
// entry chunk that non-animated pages (landing, auth) have to parse.


/**
 * GA4 (gtag.js) + Meta Pixel, loaded on first interaction only.
 *
 * `dataLayer` and the `fbq` queueing stub are created synchronously, so any
 * push/track that happens before the vendor scripts arrive is queued and
 * flushed on load — no event is lost. The vendor requests themselves (gtag.js,
 * fbevents.js) wait for the first real user interaction, with **no timer
 * fallback**: on a throttled mobile CPU they cost ~1.9s of script evaluation
 * and ~570 KB, which is the single largest contributor to Total Blocking Time.
 *
 * A load that never sees an interaction is still counted — `serverPageview`
 * reports it through the Conversions API and the GA4 Measurement Protocol,
 * reusing `__lxPageViewEventId` so Meta deduplicates the two copies if the
 * visitor interacts later and the queued browser PageView flushes.
 */

const DEFERRED_TAGS_SNIPPET = `(function(w,d){
w.dataLayer=w.dataLayer||[];
var id;try{id=w.crypto&&w.crypto.randomUUID?w.crypto.randomUUID():null}catch(e){id=null}
if(!id)id='pv_'+Date.now()+'_'+Math.random().toString(36).slice(2,10);
w.__lxPageViewEventId=id;w.__lxTagsLoaded=false;
if(!w.fbq){var n=w.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!w._fbq)w._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];}
w.fbq('init','1259888142856998');w.fbq('track','PageView',{},{eventID:id});
var E=['pointerdown','keydown','touchstart','scroll'];
var loaded=false;
function inject(src){var s=d.createElement('script');s.async=true;s.src=src;var f=d.getElementsByTagName('script')[0];f.parentNode.insertBefore(s,f);}
function load(){if(loaded)return;loaded=true;w.__lxTagsLoaded=true;
inject('https://connect.facebook.net/en_US/fbevents.js');
E.forEach(function(e){w.removeEventListener(e,load,{capture:true})});
try{w.dispatchEvent(new CustomEvent('lx-tags-loaded'))}catch(e){}}
w.__lxLoadTags=load;
E.forEach(function(e){w.addEventListener(e,load,{capture:true,passive:true,once:true})});
})(window,document);`;

/**
 * Metricool tracker (social/blog analytics, hash a7198dbe88b0361d447f9421c67e059f).
 * be.js is ~3 KB, so it loads on window load (+1.2 s) rather than being
 * interaction-gated like the ad tags — it is small enough not to matter for
 * TBT but is still kept off the render path.
 */
const METRICOOL_SNIPPET = `(function(w,d){
var loaded=false;
function inject(){if(loaded)return;loaded=true;
var b=d.getElementsByTagName('head')[0];
var s=d.createElement('script');s.type='text/javascript';s.async=true;
s.src='https://tracker.metricool.com/resources/be.js';
s.onload=function(){try{if(w.beTracker&&w.beTracker.t){w.beTracker.t({hash:'a7198dbe88b0361d447f9421c67e059f'});}}catch(e){}};
b.appendChild(s);}
if(d.readyState==='complete'){setTimeout(inject,1200);}
else{w.addEventListener('load',function(){setTimeout(inject,1200);},{once:true});}
})(window,document);`;



// Loopback hosts are exempt: otherwise a local production build (used to verify
// the service worker / cache behaviour) redirects itself to the live site.
const DOMAIN_CANONICAL_SNIPPET = `(function() {var host = window.location.hostname;var protocol = window.location.protocol;var isLocal = host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';var isHttp = protocol === 'http:' && !isLocal;var isStaging = host === 'lead-bright-kids.lovable.app';var isPublishedLovableApp = host === 'leadxkids.lovable.app';var isWww = host === 'www.leadxkids.com';if (isHttp || isStaging || isPublishedLovableApp || isWww) {window.location.replace('https://leadxkids.com' + window.location.pathname + window.location.search + window.location.hash);}})();`;

const PWA_INSTALL_PROMPT_SNIPPET = `(function() {window.addEventListener('beforeinstallprompt', function(e) {e.preventDefault();window.__pwaInstallPrompt = e;window.__pwaInstallPromptCaptured = true;try {window.dispatchEvent(new CustomEvent('pwa-prompt-ready'));} catch (_) {}}, { capture: true });})();`;

// This tiny reset lives directly in the document shell, independent of the
// generated stylesheet. During preview rebuilds and cold route-chunk waits it
// prevents raw images/accessibility links from expanding across the viewport.
const SHELL_SAFETY_CSS = `
html{box-sizing:border-box}*,*::before,*::after{box-sizing:inherit}
body{margin:0}img{max-width:100%;height:auto}
.lx-skip-link{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.lx-skip-link:focus{position:fixed;width:auto;height:auto;top:8px;left:8px;margin:0;padding:8px 16px;overflow:visible;clip:auto;white-space:normal;z-index:200;background:#087f7a;color:#fff;border-radius:6px;font:600 14px/1.4 system-ui,sans-serif}
.lx-route-loading{min-height:100dvh;overflow:hidden}
.lx-route-loading img{display:none!important}
`;

const DEVICE_DETECTION_SNIPPET = `(function() {var isOAuthCallback = window.location.hash.includes('access_token') || window.location.hash.includes('error=') || window.location.search.includes('code=') || window.location.search.includes('error=');if (isOAuthCallback) {window.__isOAuthCallback = true;}var isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true || document.referrer.includes('android-app://');var w = isPWA ? Math.min(window.screen.width, window.innerWidth || window.screen.width) : window.innerWidth;window.__screenWidth = window.screen.width;window.__innerWidth = window.innerWidth;window.__isPWAFirstLoad = isPWA;var isMobile = w <= 767;var isTablet = w > 767 && w <= 1024;window.__initialDeviceWidth = w;window.__initialDeviceClass = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';document.documentElement.classList.add(isMobile ? 'device-mobile' : isTablet ? 'device-tablet' : 'device-desktop');if (isPWA) {requestAnimationFrame(function() {var newW = window.innerWidth;var newIsMobile = newW <= 767;var newIsTablet = newW > 767 && newW <= 1024;var currentClass = newIsMobile ? 'device-mobile' : newIsTablet ? 'device-tablet' : 'device-desktop';if (!document.documentElement.classList.contains(currentClass)) {document.documentElement.classList.remove('device-mobile', 'device-tablet', 'device-desktop');document.documentElement.classList.add(currentClass);}});}})();`;

// Organization / WebSite / WebPage structured data is emitted as one @graph
// per route by seoHead() (src/lib/seo/graph.ts), so the root no longer emits
// its own duplicate Organization and WebSite blocks on every page.

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Analytics ids are non-critical: never let a transient RPC failure blank the app.
  loader: async () => {
    try {
      return await getTagConfig();
    } catch {
      return { ga4MeasurementId: null, adsConversionId: null, adsLabels: {} };
    }
  },
  head: () => ({
    meta: [
      { charSet: "UTF-8" },
      {
        name: "viewport",
        // viewport-fit=cover is emitted server-side (it is inert on desktop) so
        // the device-detection script never has to mutate this tag after SSR —
        // that mutation was a hydration-mismatch source on mobile landing.
        content:
          "width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes, viewport-fit=cover",
      },
      { name: "HandheldFriendly", content: "true" },
      { name: "MobileOptimized", content: "width" },
      // SEARCH ENGINE VERIFICATION — paste codes here (content value only).
      // Google Search Console (HTML tag method):
      { name: "google-site-verification", content: "sHwuU-pCon7lUd6VslkZjDNk5qSl8EoLHclmNs5oT1Q" },
      // Bing Webmaster Tools is verified via Google Search Console import (no meta tag needed).
      { name: "theme-color", content: "#087F7A" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "LeadXkids" },
      { name: "author", content: "LeadXkids" },
      { title: SITE_TITLE },
      { name: "description", content: SITE_DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:title", content: SITE_TITLE },
      { property: "og:description", content: SITE_DESCRIPTION },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: OG_IMAGE_ALT },
      { property: "og:site_name", content: "LeadXkids" },
      { property: "og:locale", content: "en_IN" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: SITE_TITLE },
      { name: "twitter:description", content: SITE_DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: OG_IMAGE_ALT },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png?v=3.3.0" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png?v=3.3.0" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png?v=3.3.0" },
      { rel: "shortcut icon", type: "image/png", href: "/favicon.png?v=3.3.0" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=3.3.0" },
      { rel: "manifest", href: "/manifest.json?v=3.3.0" },
      { rel: "dns-prefetch", href: "https://jstejykrmtqpwzlauxdx.supabase.co" },
      { rel: "preconnect", href: "https://jstejykrmtqpwzlauxdx.supabase.co", crossOrigin: "anonymous" },
      // Fonts are self-hosted (see @font-face in src/styles.css) and both
      // families have metric-matched `size-adjust` fallbacks, so text lays out
      // at final metrics on the first paint. Preloading the two woff2 files
      // (75 KB) therefore bought no visual gain while competing with the
      // render-blocking CSS on a slow mobile pipe — they now load on demand
      // from the stylesheet instead.

    ],
    scripts: [
      // GA4 gtag.js + Meta Pixel — queued immediately, loaded after first
      // paint (or on first interaction) so they stay off the critical path.
      { children: DEFERRED_TAGS_SNIPPET },
      // Metricool analytics — small, loads after window load
      { children: METRICOOL_SNIPPET },
      // Canonical-host redirect (lovable.app → leadxkids.com); no-ops on preview/localhost
      { children: DOMAIN_CANONICAL_SNIPPET },
      // Capture beforeinstallprompt at the earliest possible point
      { children: PWA_INSTALL_PROMPT_SNIPPET },
      // PWA-aware device detection — runs before CSS/JS settle
      { children: DEVICE_DETECTION_SNIPPET },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: () => (
    <Suspense fallback={null}>
      <NotFound />
    </Suspense>
  ),
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <style dangerouslySetInnerHTML={{ __html: SHELL_SAFETY_CSS }} />
        <HeadContent />
      </head>
      <body>
        {/* Meta Pixel (noscript) */}
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            alt=""
            src="https://www.facebook.com/tr?id=1259888142856998&ev=PageView&noscript=1"
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const router = useRouter();
  useEffect(() => router.subscribe('onResolved', () => captureAttribution()), [router]);
  const { queryClient } = Route.useRouteContext();
  const tagConfig = Route.useLoaderData();
  // GA4 + Google Ads are configured in code (no tag manager); keep the
  // configuration call after render so the tracking implementation is not in
  // the initial route module graph.
  useEffect(() => {
    if (tagConfig) {
      void import("@/lib/ga4").then(({ initGa4 }) => initGa4(tagConfig));
    }
  }, [tagConfig]);
  // Keeps installed PWAs / long sessions from sitting on a stale bundle.
  // Warm the next page's chunk on hover/touch, so navigation does not flash a
  // loading state while the chunk downloads.
  useEffect(() => {
    void import("@/lib/linkPrefetch").then(({ installLinkPrefetch }) => installLinkPrefetch());
  }, []);
  // Article trial-CTA clicks are sent as a window event so article content
  // modules never import browser analytics (keeps sitemap + SSR graphs clean).
  useEffect(() => {
    const onCta = (e: Event) => {
      const detail = (e as CustomEvent<Record<string, unknown>>).detail ?? {};
      void Promise.all([import("@/lib/ctaTracking"), import("@/lib/organicCtaParams")]).then(
        ([{ trackCtaClick }, { organicCtaParams }]) =>
          trackCtaClick(
            "organic_content_trial_cta_click",
            organicCtaParams({
              pageSlug: String(detail.page_slug ?? ""),
              placement: String(detail.placement ?? "contextual"),
              destination: String(detail.destination ?? ""),
              pagePath: window.location.pathname,
              referrer: document.referrer,
            }),
          ),
      );
    };
    window.addEventListener("lx:organic-trial-cta", onCta);
    return () => window.removeEventListener("lx:organic-trial-cta", onCta);
  }, []);
  useEffect(() => {
    void import("@/lib/pwa").then(({ initPWA }) => initPWA());
    // The app rendered, so a chunk-miss reload (if any) worked: re-arm the
    // one-reload budget for the next deploy. See src/lib/lazyPage.ts.
    clearChunkReloadGuard();
  }, []);

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Sonner />
              <ViewAsProvider>
                <>
                  <SkipToContent />
                  <ScrollToTop />
                  <AdminBodyClass />
                  <PaymentRouteCleanup />
                  <DeferredGlobalTracking />
                  <DeferredGlobalUI />
                  <Suspense fallback={null}>
                    <Ga4DebugPanelGate />
                  </Suspense>

                  <Suspense fallback={<DelayedFallback />}>
                    <RouteErrorBoundary>
                      <AppNavigationShell><Outlet /></AppNavigationShell>
                    </RouteErrorBoundary>
                  </Suspense>
                </>
              </ViewAsProvider>
            </TooltipProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

function RootErrorComponent({ error: rawError, reset }: { error: unknown; reset: () => void }) { const error = rawError instanceof Error ? rawError : new Error(String(rawError));
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      <h1 className="text-2xl font-bold text-foreground">This page didn't load</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        Something went wrong while loading this page. You can try again, or head back home.
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => {
            void router.invalidate();
            reset();
          }}
          className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
        >
          Try again
        </button>
        <a
          href="/"
          className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground"
        >
          Go home
        </a>
      </div>
    </div>
  );
}
